import React, { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { Box, Typography } from '@mui/material';
import { NumericFormat } from 'react-number-format';

function StackedBarCardUsageChart({ barData, categories, selectedCategories, sortedCases, setLogsForExploration, projectedPerCaseDisplay, avgPerCaseDisplay, card }) {
    // compute bar chart max value based on selection so axis rescales when filtering to one category
    const barValueMax = useMemo(() => {
        if (!barData || barData.length === 0) return card && card.factored_cost ? (card.factored_cost / 100) * 1.05 : 0;
        const dataMaxAll = Math.max(...barData.map(d =>
            categories.reduce((sum, cat) => sum + (d[cat] || 0), 0)
        ));
        if (selectedCategories && selectedCategories.length > 0) {
            const dataMaxSelected = Math.max(...barData.map(d =>
                selectedCategories.reduce((s, cat) => s + (d[cat] || 0), 0)
            ));
            return Math.max(dataMaxSelected, projectedPerCaseDisplay || 0, avgPerCaseDisplay || 0) * 1.05;
        }
        return Math.max(dataMaxAll, projectedPerCaseDisplay || 0, avgPerCaseDisplay || 0) * 1.05;
    }, [barData, categories, selectedCategories, projectedPerCaseDisplay, avgPerCaseDisplay, card]);

    return (
        <ResponsiveBar
            height={400}
            data={barData}
            keys={selectedCategories && selectedCategories.length > 0 ? selectedCategories : categories}
            indexBy="caseLabel"
            onClick={(datum) => {
                // datum.data.caseLabel is 'case-<idx>'
                const label = datum?.data?.caseLabel || datum?.indexValue || datum;
                const m = (label || '').toString().match(/case-(\d+)/);
                if (m) {
                    const caseIndex = parseInt(m[1], 10);
                    const selectedCase = sortedCases[caseIndex];
                    if (selectedCase) setLogsForExploration(selectedCase.logs);
                }
            }}
            margin={{ top: 50, right: 0, bottom: 50, left: 60 }}
            padding={0.3}
            valueScale={{ 
                type: 'linear',
                max: barValueMax
            }}
            indexScale={{ type: 'band', round: true }}
            colors={({ id }) => {
                const categoryIndex = categories.indexOf(id);
                return selectedCategories && selectedCategories.length > 0 && !selectedCategories.includes(id)
                    ? `hsl(${(categoryIndex * 137.5) % 360}, 30%, 80%)` // Faded color
                    : `hsl(${(categoryIndex * 137.5) % 360}, 70%, 50%)`; // Normal color
            }}
            layers={[
                'grid',
                'axes',
                'bars',
                'markers',
                'legends',
                'annotations',
                // Custom layer for projected spend and average lines
                ({ innerWidth, innerHeight, yScale }) => {
                    const projectedSpend = projectedPerCaseDisplay; // use computed projected per-case display
                    const avgSpend = avgPerCaseDisplay;
                    const projectedY = yScale(projectedSpend);
                    const avgY = yScale(avgSpend);

                    return (
                        <g>
                            {/* projected spend dashed red line */}
                            <line
                                x1={0}
                                y1={projectedY}
                                x2={innerWidth}
                                y2={projectedY}
                                stroke="#e74c3c"
                                strokeWidth={2}
                                strokeDasharray="8,4"
                            />
                            <text
                                x={innerWidth - 5}
                                y={projectedY - 5}
                                textAnchor="end"
                                fill="#e74c3c"
                                fontSize="12"
                                fontWeight="bold"
                            >{`Expected: $${projectedSpend.toFixed(2)}`}</text>

                            {/* average spend solid blue line */}
                            <line
                                x1={0}
                                y1={avgY}
                                x2={innerWidth}
                                y2={avgY}
                                stroke="#2b7be4"
                                strokeWidth={2}
                            />
                            <text
                                x={5}
                                y={avgY - 5}
                                textAnchor="start"
                                fill="#2b7be4"
                                fontSize="12"
                                fontWeight="bold"
                            >{`Avg: $${avgSpend.toFixed(2)}`}</text>
                        </g>
                    );
                }
            ]}
            defs={[
                {
                    id: 'dots',
                    type: 'patternDots',
                    background: 'inherit',
                    color: '#38bcb2',
                    size: 4,
                    padding: 1,
                    stagger: true
                },
                {
                    id: 'lines',
                    type: 'patternLines',
                    background: 'inherit',
                    color: '#eed312',
                    rotation: -45,
                    lineWidth: 6,
                    spacing: 10
                }
            ]}
            borderColor={{
                from: 'color',
                modifiers: [
                    [
                        'darker',
                        1.6
                    ]
                ]
            }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -45,
                legend: 'Date',
                legendPosition: 'middle',
                legendOffset: 42,
                format: (value) => {
                    try {
                        // `value` is the `caseLabel` index; find original date from barData
                        const found = barData.find(b => b.caseLabel === value);
                        const d = found ? new Date(found._date) : new Date(value);
                        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                    } catch (e) {
                        return value;
                    }
                }
            }}
            axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Cost (USD)',
                legendPosition: 'middle',
                legendOffset: -40,
                format: (value) => `$${value.toFixed(0)}`
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{
                from: 'color',
                modifiers: [
                    [
                        'darker',
                        1.6
                    ]
                ]
            }}
            tooltip={({ id, value, color, data }) => (
                <Box
                    sx={{
                        backgroundColor: 'white',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        <span style={{ color: color, fontWeight: 'bold' }}>●</span> {id}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Date:</strong> {new Date(data._date || data.x).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                        <strong>Cost:</strong>{' '}
                        <NumericFormat
                            value={value}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'$'}
                            decimalScale={2}
                            fixedDecimalScale={true}
                        />
                    </Typography>
                </Box>
            )}
        />
    );
}

export default StackedBarCardUsageChart;
